import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import clsx from 'clsx';

import client from 'api/apollo-client';
import { getServerPageGetThemes, getServerPageGetWooRequests } from 'api/graphql-helper';
import { Theme, WooRequestOverviewFieldsFragment } from 'api/graphql-types';
import {
	setWooRequests,
	setWooRequestsCount,
	setIsLoading,
	setCurrentPage,
	setSort,
	resetStore
} from 'redux/stores/woo-requests';
import { useAppSelector } from 'redux/store';
import { Pagination } from 'components/pagination';
import { EmptyState } from 'components/empty-state/empty-state';
import { Sort } from 'components/sort';
import { Button } from 'components/button';
import { CardDownload } from 'components/card';
import { Icon } from 'components/icon/icon';
import { FilterButton, IFilterItem } from 'components/filter-button/filter-button';
import { getInitialFilterItem } from 'utils/get-initial-filter-item';
import useDidMountEffect from 'utils/hooks/use-mount-effect';
import { basicSlugify } from 'utils/slugify';

import IconSelect from 'public/assets/icons/union.svg';

import styles from './woo-request-overview.module.scss';

export interface IWooRequestOverviewProps {
	module?: WooRequestOverviewFieldsFragment;
}

export const WooRequestOverview = ({ module }: IWooRequestOverviewProps): React.JSX.Element => {
	const { publicationState } = useAppSelector((state) => state.settings);
	const intl = useIntl();
	const dispatch = useDispatch();
	const router = useRouter();
	const overviewRef = useRef<HTMLDivElement>(null);
	const [themes, setThemes] = useState<Theme[]>([]);
	const [selectedThemes, setSelectedThemes] = useState<IFilterItem[]>([]);
	const { locale } = router;
	const { currentPage, wooRequests, wooRequestsCount, isLoading, sort } = useAppSelector((state) => state.wooRequests);

	useEffect(() => {
		if (router.isReady) {
			if (router?.query?.themeIds) setIsLoading(true);

			getThemes();
		}
	}, [router.isReady]);

	const getThemes = async (): Promise<void> => {
		const result = await (
			await getServerPageGetThemes({ variables: { locale: router.locale } }, client)
		).props.data.themes;

		if (result) {
			const newThemes = result as Theme[];
			setThemes(newThemes);

			const initialFilter = getInitialFilterItem(
				newThemes.map((item) => {
					return {
						id: item.documentId || '',
						title: item?.title || ''
					};
				}),
				router?.query?.themeIds
			);

			setSelectedThemes(initialFilter);
			getWooRequestData([...selectedThemes, ...initialFilter].map((item) => String(item.id)));
			setIsLoading(false);
		}
	};

	const limit = 10;
	const start = (currentPage - 1) * limit;
	const totalNumberOfPages = Math.ceil(wooRequestsCount / limit);
	const hasResults = wooRequests && wooRequests.length > 0;
	const sortOptions = [
		{
			id: 'startDate:DESC',
			label: intl.formatMessage({ id: 'modules.wooRequestOverview.sortNewToOld' }),
			shortLabel: intl.formatMessage({ id: 'modules.wooRequestOverview.newToOld' })
		},
		{
			id: 'startDate:ASC',
			label: intl.formatMessage({ id: 'modules.wooRequestOverview.sortOldToNew' }),
			shortLabel: intl.formatMessage({ id: 'modules.wooRequestOverview.oldToNew' })
		}
	];

	const [sortIsOpen, setSortIsOpen] = useState<boolean>(false);

	const getWooRequestData = async (themeIds: string[]): Promise<void> => {
		dispatch(setIsLoading(true));
		try {
			const result = await getServerPageGetWooRequests(
				{
					variables: {
						limit,
						start,
						locale,
						sort: [String(sort.id), 'id:ASC'],
						themeIds: themeIds.length === 0 ? undefined : themeIds.map((item) => item.toString()),
						publicationState
					}
				},
				client
			);
			const { wooRequests_connection: wooRequestItems } = result.props.data;

			const mappedWooRequestItems = wooRequestItems?.data
				? wooRequestItems?.data?.map((item) => item.attributes).filter(Boolean)
				: [];

			dispatch(setWooRequests(mappedWooRequestItems));
			dispatch(setWooRequestsCount(wooRequestItems?.pageInfo.total || 0));
			dispatch(setIsLoading(false));
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log('error', error);
		}
	};

	const scrollToTop = (): void => {
		if (typeof window !== 'undefined') {
			const extraOffset = 120;
			const moduleOffset = (overviewRef.current?.getBoundingClientRect().top || 0) + window.pageYOffset - extraOffset;
			window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
		}
	};

	const handlePageChange = (page: number): void => {
		if (page !== currentPage) {
			scrollToTop();
			dispatch(setCurrentPage(page));
		}
	};

	const handleSortOnChange = (selectedSortFilter: { id: string; label: string } | undefined): void => {
		if (selectedSortFilter) dispatch(setSort(selectedSortFilter));
	};

	useDidMountEffect(() => {
		getWooRequestData(selectedThemes.map((item) => String(item.id)));
	}, [sort, currentPage, currentPage, locale]);

	const handleFilterChange = (): void => {
		getWooRequestData(selectedThemes.map((item) => String(item.id)));
	};

	useEffect(() => {
		if (sortIsOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'visible';
		}
	}, [sortIsOpen]);

	useEffect(() => {
		return () => {
			dispatch(resetStore());
		};
	}, []);

	return (
		<div className="container" ref={overviewRef}>
			<div className={styles.header}>
				<h2>{module?.sectionTitle || intl.formatMessage({ id: 'modules.wooRequestOverview.title' })}</h2>
				<div className={styles.filterBar}>
					<div className={styles.filterContent}>
						{module?.filterLabel && <span className={styles.label}>{module.filterLabel}</span>}
						<div className={styles.filterButtons}>
							<span className={styles.themeFilter}>
								<FilterButton
									title={intl.formatMessage({ id: 'modules.projectOverview.themeFilter.title' })}
									items={
										themes?.map((theme) => ({ id: theme.documentId || '', label: theme?.attributes?.title || '' })) ||
										[]
									}
									onChange={handleFilterChange}
									onSelectedItemsChanged={setSelectedThemes}
									selectedItems={selectedThemes}
								/>
							</span>
							{!!selectedThemes.length && (
								<span className={styles.removeFilters}>
									<Button
										title={intl.formatMessage(
											{ id: 'modules.projectOverview.deleteFilters' },
											{ amount: selectedThemes.length }
										)}
										variant="tertiary-dark"
										onClick={() => {
											setSelectedThemes([]);
											getWooRequestData([]);
										}}
									/>
								</span>
							)}
						</div>
					</div>
					<span className={styles.sortButton}>
						<Button
							variant="secondary-light"
							title={intl.formatMessage({ id: 'global.sort' })}
							icon={<Icon customIcon={<IconSelect />} size="14px" />}
							onClick={() => setSortIsOpen(!sortIsOpen)}
						/>
					</span>
					<Sort
						onChange={handleSortOnChange}
						mobileIsOpen={sortIsOpen}
						setMobileIsOpen={setSortIsOpen}
						customOptions={sortOptions}
						stateType="wooRequests"
					/>
				</div>
			</div>
			<div className={clsx(styles.results, hasResults && isLoading && styles.resultsLoading)}>
				{hasResults ? (
					wooRequests?.map(({ title, zip, startDate }) => (
						<CardDownload key={`woo-request-${basicSlugify(title)}`} title={title} date={startDate} zip={zip} />
					))
				) : (
					<EmptyState />
				)}
			</div>

			<Pagination currentPage={currentPage} onPageChange={handlePageChange} totalPages={totalNumberOfPages} />
		</div>
	);
};

export default WooRequestOverview;
